import React from 'react';
import { SignaGlyph } from '../pa/SignaGlyph';

/**
 * Sticky composer at the bottom of the Today column. Input + three capture
 * icon buttons (note, todo, email) + send.
 *
 * For R2 only the send button is wired; capture icons dispatch custom events
 * that AppShell can listen for if or when the mini-modes are built.
 */

export interface ComposerProps {
  value: string;
  onChange: (v: string) => void;
  onSubmit: () => void;
  disabled?: boolean;
  placeholder?: string;
  inputRef?: React.RefObject<HTMLInputElement>;
}

export function Composer({
  value,
  onChange,
  onSubmit,
  disabled = false,
  placeholder = 'Ask, capture, or give me a task...',
  inputRef,
}: ComposerProps) {
  const canSend = value.trim().length >= 2 && !disabled;

  const handleKey = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      if (canSend) onSubmit();
    }
  };

  return (
    <div
      style={{
        position: 'sticky',
        bottom: 20,
        marginTop: 36,
        background: 'var(--bg-surface)',
        border: '1px solid var(--border)',
        borderRadius: 14,
        padding: '10px 10px 10px 14px',
        display: 'flex',
        alignItems: 'center',
        gap: 10,
        boxShadow: 'var(--shadow-md)',
      }}
    >
      <SignaGlyph size={16} />
      <input
        ref={inputRef}
        type="text"
        value={value}
        onChange={(e) => onChange(e.target.value)}
        onKeyDown={handleKey}
        placeholder={placeholder}
        disabled={disabled}
        style={{
          flex: 1,
          fontFamily: 'var(--font-ui)',
          fontSize: 14.5,
          background: 'transparent',
          border: 'none',
          outline: 'none',
          color: 'var(--text-primary)',
          padding: '8px 0',
        }}
      />
      <div style={{ display: 'flex', gap: 4 }}>
        <IconButton
          title="Capture a note"
          onClick={() => window.dispatchEvent(new Event('signa:new-note'))}
        >
          <NoteIcon />
        </IconButton>
        <IconButton
          title="New todo"
          onClick={() => window.dispatchEvent(new Event('signa:new-todo'))}
        >
          <TodoIcon />
        </IconButton>
        <IconButton
          title="Compose email"
          onClick={() =>
            window.dispatchEvent(
              new CustomEvent('signa:compose', {
                detail: { to: '', subject: '', body: '' },
              })
            )
          }
        >
          <MailIcon />
        </IconButton>
      </div>
      <button
        type="button"
        onClick={onSubmit}
        disabled={!canSend}
        style={{
          padding: '7px 14px',
          fontFamily: 'var(--font-ui)',
          fontSize: 13,
          fontWeight: 500,
          background: canSend ? 'var(--accent)' : 'var(--bg-active)',
          color: canSend ? 'var(--text-inverse)' : 'var(--text-tertiary)',
          border: 'none',
          borderRadius: 8,
          cursor: canSend ? 'pointer' : 'default',
          transition: 'all 0.15s',
        }}
      >
        Send
      </button>
    </div>
  );
}

function NoteIcon() {
  return (
    <svg
      aria-hidden="true"
      viewBox="0 0 20 20"
      width="15"
      height="15"
      fill="none"
      stroke="currentColor"
      strokeWidth="1.7"
      strokeLinecap="round"
      strokeLinejoin="round"
    >
      <path d="M5.5 3.5h6.75L16 7.25V16a1.5 1.5 0 0 1-1.5 1.5h-9A1.5 1.5 0 0 1 4 16V5a1.5 1.5 0 0 1 1.5-1.5Z" />
      <path d="M12 3.75V7.5h3.75" />
      <path d="M7.25 10h5.5" />
      <path d="M7.25 13.25h4" />
    </svg>
  );
}

function TodoIcon() {
  return (
    <svg
      aria-hidden="true"
      viewBox="0 0 20 20"
      width="15"
      height="15"
      fill="none"
      stroke="currentColor"
      strokeWidth="1.7"
      strokeLinecap="round"
      strokeLinejoin="round"
    >
      <rect x="3.5" y="3.5" width="13" height="13" rx="3" />
      <path d="m7 10 2 2 4-4" />
    </svg>
  );
}

function MailIcon() {
  return (
    <svg
      aria-hidden="true"
      viewBox="0 0 20 20"
      width="15"
      height="15"
      fill="none"
      stroke="currentColor"
      strokeWidth="1.7"
      strokeLinecap="round"
      strokeLinejoin="round"
    >
      <rect x="3" y="5" width="14" height="10" rx="2" />
      <path d="m4 6 6 5 6-5" />
    </svg>
  );
}

function IconButton({
  children,
  title,
  onClick,
}: {
  children: React.ReactNode;
  title: string;
  onClick: () => void;
}) {
  return (
    <button
      type="button"
      title={title}
      onClick={onClick}
      style={{
        width: 30,
        height: 30,
        border: 'none',
        background: 'transparent',
        color: 'var(--text-tertiary)',
        borderRadius: 6,
        cursor: 'pointer',
        fontSize: 0,
        transition: 'all 0.12s',
        fontFamily: 'var(--font-ui)',
        display: 'inline-flex',
        alignItems: 'center',
        justifyContent: 'center',
      }}
      onMouseEnter={(e) => {
        e.currentTarget.style.background = 'var(--bg-hover)';
        e.currentTarget.style.color = 'var(--accent)';
      }}
      onMouseLeave={(e) => {
        e.currentTarget.style.background = 'transparent';
        e.currentTarget.style.color = 'var(--text-tertiary)';
      }}
    >
      {children}
    </button>
  );
}
